import { Trip } from "@workspace/domain/src/features/trip";
import Decimal from "decimal.js";
import * as z from "zod";

const TripSchema = z.object({
  id: z.string(),

  serial: z.string(),

  // General information
  name: z.string(),
  description: z.any().nullable(),
  locations: z.array(
    z.object({
      locationId: z.string(),
      nameEn: z.string(),
      nameAr: z.string(),
    }),
  ),
  startDate: z.date(),
  endDate: z.date(),
  status: z.enum(Trip.Status),
  hotelName: z.string().nullable(),
  hotelLocation: z.string().nullable(),

  // Tags
  audience: z.enum(Trip.Audience),
  food: z.enum(Trip.Food),
  accessibility: z.array(z.enum(Trip.Accessibility)),
  transportation: z.array(z.enum(Trip.Transportation)),
  guides: z.array(z.enum(Trip.Guides)),

  // Images
  imageKeys: z.array(z.string()),

  // Pricing
  adultPrice: z.number(),
  childPrice: z.number(),
  childMaximumAge: z.number(),
  groupPricing: z.array(
    z.object({
      id: z.string(),
      adultPrice: z.number(),
      childPrice: z.number(),
      minimumNumberOfPeople: z.number(),
    }),
  ),

  // Reservation
  adultReservationFee: z.number(),
  childReservationFee: z.number(),
  reservationDeadline: z.date(),

  // Cancellation
  cancellationDeadline: z.date().nullable(),
  adultCancellationFee: z.number(),
  childCancellationFee: z.number(),

  spotsLeft: z.number(),

  agency: z.object({
    name: z.string(),
    logoUrl: z.string().nullable(),
    phoneNumbers: z.array(z.string()),
  }),

  agencyPublicKey: z.string().nullable(),

  // Relations
  agencyId: z.string(),

  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

const SortBy = ["createdAt", "startDate", "adultPrice"] as const;
const SortOrder = ["asc", "desc"] as const;

const GetSchema = z.object({
  id: z.string(),
  getAgencyNIdStatus: z.boolean().optional(),
});

const GetAllSchema = z
  .object({
    paginate: z
      .object({
        id: z.string(),
        cursor: z.union([z.number(), z.string(), z.date()]),
      })
      .optional(),
    limit: z.number(),

    sortBy: z.enum(SortBy),
    sortOrder: z.enum(SortOrder),
  })
  .extend(
    z
      .object({
        agencyId: z.string(),
        search: z.string(),
        locations: z.array(z.string()),
        status: z.array(z.enum(Trip.Status)),

        dateMin: z.date(),
        dateMax: z.date(),

        adultPriceMin: z.number().transform((val) => new Decimal(val).times(100).toNumber()),
        adultPriceMax: z.number().transform((val) => new Decimal(val).times(100).toNumber()),

        audience: z.array(z.enum(Trip.Audience)),
        food: z.array(z.enum(Trip.Food)),
        accessibility: z.array(z.enum(Trip.Accessibility)),
        transportation: z.array(z.enum(Trip.Transportation)),
        guides: z.array(z.enum(Trip.Guides)),
        ids: z.array(z.string()),
      })
      .partial().shape,
  );

const GetByIdsSchema = z.object({
  ids: z.array(z.string()),
});

export const ClientTrip = {
  SortBy: SortBy,
  SortOrder: SortOrder,
  Trip: TripSchema,
  Get: GetSchema,
  GetAll: GetAllSchema,
  GetByIds: GetByIdsSchema,
};
export type ClientTrip = {
  SortBy: (typeof SortBy)[number];
  SortOrder: (typeof SortOrder)[number];
  Trip: z.infer<typeof TripSchema>;
  Get: z.infer<typeof GetSchema>;
  GetAll: z.infer<typeof GetAllSchema>;
  GetByIds: z.infer<typeof GetByIdsSchema>;
};
